import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@tellescope/tanstack-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Separator,
} from "@tellescope/ui";
import { SSOLoginButton } from "./SSOLoginButton";
import { GoogleLoginButton } from "./GoogleLoginButton";
import { useMobileAppLogin, MOBILE_RETURN_SCHEME_KEY } from "./useMobileAppLogin";

export const MobileAppLoginPage = () => {
  const { t } = useTranslation("auth");
  const { isValid, returnScheme, ssoRedirectUrl, onAuthSuccess, beginSSOWithReturnScheme } = useMobileAppLogin();
  const { login, loginError: error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (!isValid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">{t("Invalid mobile app link")}</CardTitle>
            <CardDescription>{t("Please return to the app and try again.")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setIsLoading(true);
    try {
      const result = await login({ email, password });
      if (result.requiresMFA) {
        sessionStorage.setItem(MOBILE_RETURN_SCHEME_KEY, returnScheme);
        navigate("/mobile-app/mfa", { replace: true });
      } else {
        onAuthSuccess(result.authToken);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t("Welcome Back")}</CardTitle>
          <CardDescription>{t("Sign in to continue to the mobile app")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("Email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("Password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {typeof error === "string" ? error : error.message}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading || !email || !password}>
              {isLoading ? t("Signing in...") : t("Sign In")}
            </Button>
          </form>

          <div className="relative my-4">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              {t("or")}
            </span>
          </div>

          <div className="space-y-2">
            <GoogleLoginButton onAuthSuccess={onAuthSuccess} returnScheme={returnScheme} />
            <SSOLoginButton
              provider="microsoft-oidc"
              redirectUrl={ssoRedirectUrl}
              onBeforeRedirect={beginSSOWithReturnScheme}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
